/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { Drawer, IModuleProps, INodeProps, Module } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ProductSpecificationTableRow } from './components/vinhuset-product-specification-table-stucture';
import { IVinhusetProductSpecificationData } from './vinhuset-product-specification.data';
import { displayStyle, IVinhusetProductSpecificationProps } from './vinhuset-product-specification.props.autogenerated';
import { getAttributeStringOrDefault, getAttributesLocale, parseAttributesMap } from '../../shared-utils/attributes';
import { AttributesForSelectedVariantInput, getAttributesForSelectedVariantAction } from '../../actions/get-attributes-for-selected-variant';
import { getIconForFitsWith } from './common/icons';
import CircleProgress from './components/cricle-progress';
import { sortMediaLocations } from './common/media-location-sorter';

/**
 * Maintain Drawer State.
 */
export interface IProductSpecificationState {
    isDrawerOpen: boolean;
    drawerListId: number | undefined;
}

/**
 * Product specification view props.
 */
export interface IProductSpecificationViewProps extends IVinhusetProductSpecificationProps<IVinhusetProductSpecificationData> {
    ProductSpecification: IModuleProps;
    ProductSpecificationTableProps: INodeProps;
    ProductSpecificationTableBodyProps: INodeProps;
    productSpecificationResult?: (JSX.Element | null)[];
    title?: React.ReactNode;
    isAccrodion?: boolean;
    handleText?(event: Msdyn365.ContentEditableEvent): void;
    addionalMedia: React.ReactNode;
    curatedInformation: React.ReactNode;
    footer: React.ReactNode;
}

/**
 * Check for display style either accordion or table.
 */
export interface IProductSpecificationExtentedProps extends IVinhusetProductSpecificationProps<IVinhusetProductSpecificationData> {
    displayStyle?: displayStyle;
}

/**
 *
 * ProductSpecification component.
 * @extends {React.Component<IVinhusetProductSpecificationProps<IVinhusetProductSpecificationData>>}
 */
@observer
class ProductSpecification extends React.Component<IProductSpecificationExtentedProps, IProductSpecificationState> {

    private readonly _attributesLocale = getAttributesLocale(this.props.context.request.locale);

    public constructor(props: IProductSpecificationExtentedProps) {
        super(props);
        this.state = {
            isDrawerOpen: false,
            drawerListId: undefined
        };
    }

    public async componentDidMount(): Promise<void> {
        const { context, data, telemetry } = this.props;
        const catalogId = Msdyn365.getCatalogId(this.props.context.request);

        console.log('attrs', JSON.parse(JSON.stringify(this.props.data.productSpecificationData.result || {})));

        reaction(
            () => this.props.data.productSpecificationData.result,
            () => {
                console.log('attrs', JSON.parse(JSON.stringify(this.props.data.productSpecificationData.result || {})));
            }
        );

        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            () => {
                if (context && data.product.result) {
                    const actionInput = new AttributesForSelectedVariantInput(
                        context.request.locale,
                        data.product.result.RecordId,
                        +context.actionContext.requestContext.apiSettings.channelId,
                        data.product.result,
                        catalogId
                    );

                    getAttributesForSelectedVariantAction(actionInput, this.props.context.actionContext).catch((error: Error) => {
                        telemetry.error(error.message);
                        telemetry.debug('Unable to update attributes for Product');
                        telemetry.error(error.message);
                    });
                }
            }
        );
    }

    public shouldComponentUpdate(nextProps: IProductSpecificationExtentedProps, nextState: IProductSpecificationState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        let data: IVinhusetProductSpecificationData;
        try {
            data = this.props.data;
        } catch (error) {
            this.props.telemetry.error(`Something went wrong ------${error}`);
            return <div>{error}</div>;
        }

        if (!data.productSpecificationData.result || data.productSpecificationData.result.length <= 0) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            return null;
        }

        const { heading, className } = this.props.config;
        const { productSpecificationData } = this.props.data;
        const isAccrodion = this.props.config.displayStyle === displayStyle.accordion;
        const viewProps = {
            ...this.props,
            handleText: this.handleHeadingChange,
            ProductSpecification: {
                moduleProps: this.props,
                className: classnames('ms-product-specification', className)
            },
            ProductSpecificationTableProps: isAccrodion
                ? {
                      moduleProps: this.props,
                      className: classnames('ms-product-specification__accordion', className)
                  }
                : {
                      className: classnames('ms-product-specification__table table-striped table-bordered'),
                      tag: 'table'
                  },
            isAccrodion,
            ProductSpecificationTableBodyProps: isAccrodion
                ? {}
                : {
                      className: '',
                      tag: 'tbody'
                  },
            title: heading?.text && (
                <Msdyn365.Text
                    className='ms-product-specification__heading'
                    tag={heading.tag ?? 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),

            productSpecificationResult: isAccrodion
                ? productSpecificationData.result && this._createDrawerBody(data)
                : productSpecificationData.result && this._createTableBody(data),
            addionalMedia: this._renderAddionalMedia(),
            curatedInformation: this._renderCuratedInformation(),
            footer: null
        } as IProductSpecificationViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle Heading Text Change.
     * @param event - Current event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    private _createTableBody(content: IVinhusetProductSpecificationData): { heading: any; tableData: any }[] {
        const productSpecificationData = content.productSpecificationData.result;
        const attributesMap = parseAttributesMap(this.props.context, productSpecificationData);
        const tables: {
            heading: JSX.Element;
            subHeader: JSX.Element | null;
            tableData: (JSX.Element | null)[];
            modelVersion?: string;
        }[] = [];

        attributesMap.forEach((group) => {
            const table = group.attributes.map((attribute: { name: string; value: string }, index: number) => {
                return (
                    <ProductSpecificationTableRow
                        className='ms-product-specification__table-row'
                        key={index}
                        productName={attribute.name}
                        cellData={<div dangerouslySetInnerHTML={{ __html: attribute.value }}></div>}
                    />
                );
            });

            // eslint-disable-next-line
            let subHeader: JSX.Element | null = null;

            tables.push({
                heading:
                    <Msdyn365.Text
                        className='ms-product-specification__heading'
                        tag={this.props.config.heading!.tag ?? 'h2'}
                        text={group.name}
                        editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                    />,
                subHeader,
                tableData: table,
                modelVersion: group.modelVersion
            });
        });

        return tables;
    }

    private _createDrawerBody(content: IVinhusetProductSpecificationData): (JSX.Element | null)[] {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };
        return content.productSpecificationData.result!.map(product => {
            const cellData: JSX.Element | null = this._renderProductCell(product);
            if (!cellData) {
                return null;
            }
            return (
                <Module {...accordionItemContainer} key={product.RecordId}>
                    <Drawer
                        key={product.RecordId}
                        collapseProps={{
                            timeout: 0,
                            isOpen: this.state.isDrawerOpen && this.state.drawerListId === product.RecordId
                        }}
                        className='ms-product-specification__drawer'
                        openGlyph='ms-product-specification__drawer-open'
                        closeGlyph='ms-product-specification__drawer-close'
                        glyphPlacement='end'
                        toggleButtonText={product.Name ?? ''}
                        onToggle={this._toggle(product.RecordId)}
                    >
                        <div className='ms-product-details'>{cellData}</div>
                    </Drawer>
                </Module>
            );
        });
    }

    private _getAssetFixed(assetPath: string): string {
        let assetUrl = Msdyn365.getAsset(assetPath, this.props.context.request);

        if (typeof window === "undefined") {
            return assetUrl;
        }

        const SUBMISSIONID = (window as any | undefined)?.___initialData___?._environmentVariable?.['SUBMISSIONID'];

        if (assetUrl.includes('00000-00000-00000-00000-00000') && SUBMISSIONID) {
            assetUrl = assetUrl.replace('00000-00000-00000-00000-00000', SUBMISSIONID);
        }

        return assetUrl;
    }

    private _getFlavorProfile(): React.ReactNode[] {
        const attributes = this.props.data.productSpecificationData.result;
        const flavorProfileAttribtues = [
            this._attributesLocale.flavorProfile.body,
            this._attributesLocale.flavorProfile.freshness,
            this._attributesLocale.flavorProfile.sweetness,
            this._attributesLocale.flavorProfile.bitterness,
            this._attributesLocale.flavorProfile.tannins,
            this._attributesLocale.flavorProfile.oak,
            this._attributesLocale.flavorProfile.smoke,
            this._attributesLocale.flavorProfile.spiceHerb,
            this._attributesLocale.flavorProfile.fruit
        ];
        const items: React.ReactNode[] = [];

        flavorProfileAttribtues.forEach((attribute, index) => {
            const attrValue = getAttributeStringOrDefault(attributes, attribute, undefined);
            if (attrValue) {
                const match = attrValue.match(/(.*?)\,\s(\d+)\sav\s(\d+)/);

                if (match?.length !== 4) {
                    return;
                }

                const label = match[1];
                const value = parseInt(match[2], 10);

                if (isNaN(value)) {
                    return;
                }

                items.push(
                    <div key={attribute}>
                        <CircleProgress value={value} />
                        <span>{label}</span>
                    </div>
                );
            }
        });

        return items;
    }

    private _renderAddionalMedia(): JSX.Element | null {
        const {
            data: { searchMediaLocation }
        } = this.props;

        const mediaLocationsToDisplay = searchMediaLocation.result?.filter((mediaLocation) => mediaLocation.IsAdditionalMedia);

        if (!mediaLocationsToDisplay || mediaLocationsToDisplay.length <= 0) {
            return null;
        }

        return (
            <div className='ms-product-specification__additional-media'>
                <ul>
                    {mediaLocationsToDisplay
                        .sort(sortMediaLocations)
                        .map((mediaLocation) => {
                            return (
                                <li key={mediaLocation.Uri}>
                                    <a href={mediaLocation.Uri} target='_blank' rel='noreferrer noopener'>
                                        {mediaLocation.AltText}
                                    </a>
                                </li>
                            );
                        }
                    )}
                </ul>
            </div>
        );
    }

    private _renderCuratedInformation(): JSX.Element | null {
        const { context, resources } = this.props;
        const attributes = this.props.data.productSpecificationData.result;

        const fitsWith = getAttributeStringOrDefault(attributes, this._attributesLocale.fitsWith, undefined);
        const flavorProfile = this._getFlavorProfile();
        const taste = getAttributeStringOrDefault(attributes, this._attributesLocale.taste, undefined);
        const rawMaterial = getAttributeStringOrDefault(attributes, this._attributesLocale.rawMaterial, undefined);
        const ingredients = getAttributeStringOrDefault(attributes, this._attributesLocale.ingredients, undefined);
        const alcoholPercent = getAttributeStringOrDefault(attributes, this._attributesLocale.alcoholPercent, undefined, '%')
            || getAttributeStringOrDefault(attributes, this._attributesLocale.alternative.alcoholPercent, undefined);
        const sugar = getAttributeStringOrDefault(attributes, this._attributesLocale.sugar, undefined)
            || getAttributeStringOrDefault(attributes, this._attributesLocale.alternative.sugar, undefined);
        const acid = getAttributeStringOrDefault(attributes, this._attributesLocale.acid, undefined);

        if (!fitsWith && flavorProfile.length === 0 && !taste && !rawMaterial && !ingredients && !alcoholPercent && !sugar && !acid) {
            return null;
        }

        return (
            <div className='ms-product-specification__curated-information' style={{ flexWrap: 'wrap' }}>
                {fitsWith && (
                    <section className='bold uppercase mobile-grid'>
                        {fitsWith.split('|').map((fitWith, index) => (
                            <div key={index}>
                                <img src={Msdyn365.getAsset(getIconForFitsWith(fitWith), context.request)} width={24} height={24} />
                                <span>{fitWith}</span>
                            </div>
                        ))}
                    </section>
                )}

                {flavorProfile.length > 0 &&(
                    <section className='bold uppercase mobile-grid'>
                        {flavorProfile}
                    </section>
                )}

                {(taste || rawMaterial || ingredients) && (
                    <section>
                        {taste && (
                            <div>
                                <img src={this._getAssetFixed('icons/wine-glass.svg')} width={30} height={30} />
                                <span>{taste}</span>
                            </div>
                        )}
                        {rawMaterial && (
                            <div>
                                <img src={this._getAssetFixed('icons/bottle.svg')} width={30} height={30} />
                                <span>{rawMaterial}</span>
                            </div>
                        )}
                        {ingredients && (
                            <div>
                                <img src={this._getAssetFixed('icons/grapes.svg')} width={30} height={30} />
                                <span>{ingredients}</span>
                            </div>
                        )}
                    </section>
                )}

                {(alcoholPercent || sugar || acid) && (
                    <section>
                        {alcoholPercent && (
                            <div>
                                <b>{resources.alcoholPercentLabel}</b> {alcoholPercent}
                            </div>
                        )}
                        {sugar && (
                            <div>
                                <b>{resources.sugarLabel}</b> {sugar}
                            </div>
                        )}
                        {acid && (
                            <div>
                                <b>{resources.acidLabel}</b> {acid}
                            </div>
                        )}
                    </section>
                )}
            </div>
        );
    }

    private readonly _toggle = (drawerListId: number | undefined) => (): void => {
        if (this.state.drawerListId !== drawerListId) {
            this.setState({ drawerListId, isDrawerOpen: true });
        } else {
            this.setState({ drawerListId: undefined, isDrawerOpen: false });
        }
    };

    private _renderProductCell(product: AttributeValue): JSX.Element | null {
        switch (product.DataTypeValue) {
            case 0: // None
                // Return span here instead of null so the row still shows
                return <span />;
            case 1: // Currency
                return this._renderCurrencyCell(product);
            case 2: // DateTime
                return this._renderDateTimeCell(product);
            case 3: // Decimal
                return this._renderDecimalCell(product);
            case 4: // Integer
                return this._renderIntegerCell(product);
            case 5: // Text
                return this._renderTextCell(product);
            case 6: // TrueFalse
                return this._renderTrueFalseCell(product);
            case 40: // Video
                this.props.telemetry.warning('Got video specification type, not supported yet so skipping line');
                return null;
            case 41: // Image
                this.props.telemetry.warning('Got image specification type, not supported yet so skipping line');
                return null;
            default:
                this.props.telemetry.warning(`Got unknown specification type ${product.DataTypeValue} so skipping line`);
                return null;
        }
    }

    private _renderCurrencyCell(product: AttributeValue): JSX.Element | null {
        if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
            return (
                <span>
                    {product.CurrencyCode}
                    {product.CurrencyValue}
                </span>
            );
        }

        return null;
    }

    private _renderDateTimeCell(product: AttributeValue): JSX.Element | null {
        if (product.DateTimeOffsetValue?.toUTCString) {
            return <span>{product.DateTimeOffsetValue.toUTCString()}</span>;
        }

        return null;
    }

    private _renderIntegerCell(product: AttributeValue): JSX.Element | null {
        if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
            return <span>{product.IntegerValue}</span>;
        }

        return null;
    }

    private _renderDecimalCell(product: AttributeValue): JSX.Element | null {
        if (product.FloatValue !== null && product.FloatValue !== undefined) {
            return <span>{product.FloatValue}</span>;
        }

        return null;
    }

    private _renderTextCell(product: AttributeValue): JSX.Element | null {
        if (product.TextValue !== null && product.TextValue !== undefined) {
            // eslint-disable-next-line react/no-danger
            return <span dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(product.TextValue)} />;
        }

        return null;
    }

    private _renderTrueFalseCell(product: AttributeValue): JSX.Element | null {
        // TODO 20369885 setting to default value is needed to work around a platform bug where
        // config string value does not get its default value.
        if (product.BooleanValue) {
            return <span>{this.props.resources.trueValueText ? this.props.resources.trueValueText : 'Yes'}</span>;
        }
        return <span>{this.props.resources.falseValueText ? this.props.resources.falseValueText : 'No'}</span>;
    }
}

export default ProductSpecification;
